import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useMarketIds, useTradesInfinite } from '@/hooks/queries/trades/use-trades';
import { useTradeFeed } from '@/hooks/use-trade-feed';
import { isNotFoundError } from '@/lib/api/error';
import type { TradesFilters } from '@/services/api/trades';
import { TRADE_TABLE_COLUMNS } from './trade-columns';
import { TradeFiltersRow } from './trade-filters';
import { TradesList } from './trades-list';

// Stable fallback — inline `?? []` would defeat DataTable's data-identity guard
const EMPTY_MARKET_IDS: string[] = [];

/**
 * Operations / Trading: cursor-paginated exchange-trade history (QuestDB) with
 * a live instant-trade feed prepended in the unfiltered view. Note the two are
 * DIFFERENT populations — the backend's `trade` broadcast is dead, so the live
 * rows are instant trades by design (flagged in the PR).
 */
export const TradingPanel = () => {
  const [filters, setFilters] = useState<TradesFilters>({});
  const marketsQuery = useMarketIds();
  const query = useTradesInfinite(filters);
  // The legacy page passed no filters to the socket hook — the ops feed sees every trade
  const { trades: liveTrades, flashTradeIds } = useTradeFeed();

  // Unknown account holder → the shared TradesList already turns the per-user endpoint's
  // 404 into an empty state; TradingPanel additionally shows a toast here,
  // which needs `filters` state the shared list doesn't have (deliberate fix)
  const isUnknownAccountHolder = Boolean(filters.accountHolderId) && query.isError && isNotFoundError(query.error);

  useEffect(() => {
    if (isUnknownAccountHolder) toast.error('Account holder not found');
  }, [isUnknownAccountHolder]);

  return (
    <div className="flex flex-col gap-4">
      <TradeFiltersRow filters={filters} marketIds={marketsQuery.data ?? EMPTY_MARKET_IDS} onChange={setFilters} />
      <TradesList
        columns={TRADE_TABLE_COLUMNS}
        emptyMessage="No results"
        endMessage="No more trades"
        errorTitle="Error loading trades"
        flashTradeIds={flashTradeIds}
        hasActiveFilters={Object.keys(filters).length > 0}
        liveTrades={liveTrades}
        query={query}
      />
    </div>
  );
};
TradingPanel.displayName = 'TradingPanel';
